import React, { useContext, useState, useEffect, useRef } from 'react'
import { View, StyleSheet, TouchableOpacity, Image, ScrollView, Platform, Linking, FlatList, useWindowDimensions } from 'react-native'
import * as ImagePicker from 'expo-image-picker'
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator'
import StyledTextInput from '../../components/StyledTextInput'
import { Icon, Text, Button, Toggle, Radio, RadioGroup, Spinner, OverflowMenu, Modal, CheckBox, Datepicker, MenuItem, Input, Select, useTheme } from '@ui-kitten/components'
import HeaderIconButton from '../../components/HeaderIconButton'
import Divider from '../../components/Divider'
import { UserContext } from '../../global/UserProvider'
import { saveNewSupply, updateSupply, deleteSupply, uploadSupplyInvoice, deleteSupplyInvoice, updateProduct, storage } from '../../global/Firebase/firebase'
import { supplyAfterPaymentWrite } from '../../global/Firebase/inventory'
import { snapshotExists } from '../../global/Firebase/types'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { captureStockGuidanceReceivingConfirmed } from '../../utils/stockGuidanceAnalytics'
import { useColors } from '../../utils/useColors'
import { findProductByBarcode } from '../../utils/barcodeScan'
import { useTranslation } from 'react-i18next'
import { productsDictAtom, productsByBarcodeDictAtom, suppliersDictAtom, currencyFormatterAtom, storesDictAtom, userRoleAtom, constantsAtom, productUnitsDictAtom, localeDateServiceAtom, organizationAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { getProductsBySupplierDict } from '../../utils/productListHelpers'
import { getLocalStoreDate, getDailyStatID } from '../../utils/timezoneFunctions'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import AdaptiveTableCard from '../../components/AdaptiveTableCard'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { exceedsStockQuantityLimit, firstUnstorableStockQuantity, MAX_STOCK_QUANTITY } from '../../utils/stockQuantityBounds'
import { apiDraftRefusalKey } from '../../global/ApiTokens/apiDraftsView'
import { reportApiDraftSaved } from '../../global/ApiTokens/reportApiDraftSaved'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

/*
Supply: {
  supplierID: string
  items: [ {
    name: string
    quantity:  
    returnQuantity
    purchasePrice 
    salePrice
    totalPurchasePrice 
    id:  
    supplierID:
  } ] 
  returnItems: []
  totalPurchaseSum:
  totalReturnSum:
  totalSum: totalPurchaseSum - totalReturnSum
  isPaid
  paymentMethod 
  userCreated
  userPaid
  dateCreated
  datePaid
  invoiceScans - [URLs]
}

*/

export function SupplyDetailScreen( { navigation, route } ) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productsDict = useAtomValue(productsDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const organization = useAtomValue(organizationAtom)
  const currency = organization?.currency.shortText || ''
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const postHog = usePostHog()
  const { detail: passedDetail, preselectedItems, preselectedStore, supplyOrderRef, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData, detailId, apiAcceptanceDraft, stockGuidance } = route.params
  const [detail, setDetail] = useState(passedDetail)
  // ATL-1445: the Atlas API v1 acceptance draft this form was opened from (its lines arrive as
  // `preselectedItems`, its store as `preselectedStore`), until the acceptance is saved.
  const [pendingApiDraft, setPendingApiDraft] = useState(passedDetail == null ? (apiAcceptanceDraft ?? null) : null)
  const [apiDraftNotice, setApiDraftNotice] = useState('')
  // ADS-INSTR-2026-003: `stockGuidance` is set only when New Sale's stock-shortage dialog opened this
  // form. Its save is reported once as `stock_unavailable_receiving_confirmed`; nothing else reads it.
  const stockGuidanceConfirmedRef = useRef(false)
  const [saveError, setSaveError] = useState('')
  const [store, setStore] = useState(detail ? detail.store : (preselectedStore || context.user.store?.id))
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [productsBySuppliers, setProductsBySuppliers] = useState({})
  const [selectedSupplier, setSelectedSupplier] = useState(
    detail != null 
      ? (suppliersDict[detail.supplierID] || {id: detail.supplierID, name: t('supplies.deletedSupplier')}) 
      : (preselectedItems?.[0] != null ? suppliersDict[preselectedItems[0].supplierID] : null)
  )
  const [items, setItems] = useState(
    detail != null 
      ? detail.items 
      : (preselectedItems != null 
        ? preselectedItems.map(item => ({
            ...item,
            quantity: item.quantity || 0,
            returnQuantity: 0,
            salePrice: productsDict[item.id]?.salePrices?.[store],
            oldPurchasePrice: productsDict[item.id]?.purchasePrice,
            oldSalePrice: productsDict[item.id]?.salePrices?.[store],
            text: productsDict[item.id]?.name,
            unit: productsDict[item.id]?.unit?.name || null,
            barcode: productsDict[item.id]?.barcode != null ? String(productsDict[item.id]?.barcode) : null,
            category: productsDict[item.id]?.categoryID || null,
          }))
        : [])
  )
  const [filteredItems, setFilteredItems] = useState(items)
  const [barcodeReturnMode, setBarcodeReturnMode] = useState(false)
  const [supplyDate, setSupplyDate] = useState(detail?.supplyDate != null ? (detail.supplyDate?.toDate?.() ? detail.supplyDate?.toDate() : detail.supplyDate) : new Date())
  const [isPaid, setIsPaid] = useState(detail != null ? detail.isPaid : false)
  const [paymentMethod, setPaymentMethod] = useState(detail != null ? detail.paymentMethod : null)
  const [paymentDate, setPaymentDate] = useState(detail?.paymentDate != null ? (detail.paymentDate?.toDate?.() ? detail.paymentDate?.toDate() : detail.paymentDate) : new Date())
  const [createPaymentTransaction, setCreatePaymentTransaction] = useState(detail && detail.createPaymentTransaction != null ? detail.createPaymentTransaction : true)
  const [invoiceScans, setInvoiceScans] = useState(detail != null ? (detail.invoiceScanRefs || []) : [])
  const [imageUploading, setImageUploading] = useState(false)
  const [isReturnPresent, setIsReturnPresent] = useState(detail != null ? detail.isReturnPresent : false)
  const [hasPricesUpdated, setHasPricesUpdated] = useState(detail != null ? detail.hasPricesUpdated : false)
  const [imagePickerMenuVisible, setImagePickerVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [modalImageURI, setModalImageURI] = useState(null)
  const [imageModalVisible, setImageModalVisible] = useState(false)
  const _datePicker = React.createRef()
  const _barcodeInput = useRef()
  const productsByBarcode = useAtomValue(productsByBarcodeDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)

  useEffect(() => {
    if (userRole?.permissions?.deleteSupply || context.user.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          detail != null && <HeaderIconButton
            onPress={async () => {
              if (!isLoading) {
                try {
                  setIsLoading(true)
                  await deleteSupply(db, detail, detail)
                  removeItemFromData?.(sectionIndex, itemIndex)
                  setIsLoading(false)
                  navigation.goBack()
                } catch(error) {
                  console.log(error)
                  setSaveError(t('error'))
                  Sentry.captureException(error, { tags: { books_write_error: 'supply-delete' } })
                }
              }
            }}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
            testID='detail-delete-button'
          />
        ),
      })
    }
  }, [isLoading])

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        let supplyRef = db.collection('supplies').doc(detailId)
        let doc = await supplyRef.get()
        if (snapshotExists(doc)) {
          let fetchedDetail = doc.data()
          fetchedDetail.ref = supplyRef
          fetchedDetail.id = supplyRef.id
          setDetail(fetchedDetail)
          setItems(fetchedDetail.items)
          setFilteredItems(fetchedDetail.items)
          setSelectedSupplier(suppliersDict[fetchedDetail.supplierID])
          setStore(fetchedDetail.store)
          setSupplyDate(fetchedDetail.supplyDate?.toDate?.() || fetchedDetail.supplyDate)
          setIsPaid(fetchedDetail.isPaid)
          setPaymentMethod(fetchedDetail.paymentMethod)
          setPaymentDate(fetchedDetail.paymentDate?.toDate?.() || fetchedDetail.paymentDate)
          setCreatePaymentTransaction(fetchedDetail.createPaymentTransaction ?? true)
          setInvoiceScans(fetchedDetail.invoiceScanRefs || [])
          setIsReturnPresent(fetchedDetail.isReturnPresent || false)
          setHasPricesUpdated(fetchedDetail.hasPricesUpdated || false)
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching document:", error);
        Sentry.captureException(error);
      }
    };

    if (detailId) {
      // Fire-and-forget by design (effect body cannot await); marked explicitly.
      void fetchDetail();
    }
  }, [detailId])

  // Debug logging for dual-write e2e tests
  React.useEffect(() => {
    if (detail) {
      console.log('[SUPPLY_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        organizationId: organization?.id,
        supplierID: detail.supplierID,
        supplierName: suppliersDict[detail.supplierID]?.name || detail.supplierID,
        totalPurchaseSum: detail.totalPurchasePrice,
        totalReturnSum: detail.totalReturnSum,
        totalSum: detail.totalSum,
        isPaid: detail.isPaid,
        paymentMethod: detail.paymentMethod,
        createPaymentTransaction: detail.createPaymentTransaction,
        isReturnPresent: detail.isReturnPresent,
        hasPricesUpdated: detail.hasPricesUpdated,
        store: detail.store,
        cashierUID: detail.cashierUID,
        items: detail.items?.map(item => ({
          id: item.id,
          name: item.name || item.text,
          quantity: item.quantity,
          returnQuantity: item.returnQuantity || 0,
          purchasePrice: item.purchasePrice,
          salePrice: item.salePrice,
        })) || [],
      })));
    }
  }, [detail, organization, suppliersDict]);

  useEffect(() => {
    if (detail == null) {
      if (preselectedItems == null) {
        let newProductsBySuppliers = getProductsBySupplierDict(productsDict, true, store)
        setProductsBySuppliers(newProductsBySuppliers)
        if (selectedSupplier) {
          setFilteredItems(newProductsBySuppliers[selectedSupplier.id])
          setItems(newProductsBySuppliers[selectedSupplier.id])
        }
      }
    } else {
      // download images — fire-and-forget by design (effect body cannot await).
      void downloadImages()
    }
  }, [store])

  const downloadImages = async () => {
    var newInvoiceScans = []
    for (const [index, value] of invoiceScans.entries()) {
      try{
        const url = await storage.child('invoices/' + value.fileName).getDownloadURL()    
        newInvoiceScans.push({...value, localUri: url})
      } catch(error) {
        Sentry.captureException(error)
        console.log(error)
      }
      
    }
    setInvoiceScans(newInvoiceScans)
  }

  async function save() {
    if (detail == null) {
      setIsLoading(true)
      if (!imageUploading && (!isPaid || (isPaid && paymentMethod))) {
        setSaveError('')
        var invoiceScanRefs = []
        invoiceScans.map((item) => invoiceScanRefs.push({fileName: item.fileName}))
        var updatedItems = []
        for (const [index, value] of items.entries()) {
          const fieldsToCheck = [
            { field: 'quantity', value: value.quantity, label: t('supplies.supplyQuantity') },
            { field: 'returnQuantity', value: value.returnQuantity, label: t('supplies.returnQuantity') },
            { field: 'purchasePrice', value: value.purchasePrice, label: t('supplies.purchasePrice') },
            { field: 'salePrice', value: value.salePrice, label: t('supplies.sellPrice') }
          ]
          
          for (const fieldData of fieldsToCheck) {
            const numberAmount = parseFloat(fieldData.value)
            if (isNaN(numberAmount)) {
              setSaveError(t('supplies.fieldNotANumberError', { 
                productName: value.name || value.text || t('notFound'),
                fieldName: fieldData.label
              }))
              setIsLoading(false)
              return
            }
            // ATL-960: the two quantity fields feed the stock ledger (product_movements.increment
            // and product_store_stock.quantity, both numeric(10,4)), so a value at or above 10^6 is
            // refused by Supabase with 22003. Firestore commits first and has no such ceiling, so
            // without this check the quantity lands in the books and the two stores disagree
            // forever. Refuse — never clamp; a substituted quantity is a number nobody entered.
            // purchasePrice/salePrice are deliberately not bounded here: they target money columns
            // whose widening is tracked separately, and inventing a bound for them now would
            // reject supplies that Postgres accepts today.
            if (
              (fieldData.field === 'quantity' || fieldData.field === 'returnQuantity') &&
              exceedsStockQuantityLimit(numberAmount)
            ) {
              setSaveError(t('supplies.fieldTooLargeError', {
                productName: value.name || value.text || t('notFound'),
                fieldName: fieldData.label,
                max: MAX_STOCK_QUANTITY
              }))
              setIsLoading(false)
              return
            }
          }
          const newValue = {
            ...value,
            quantity: parseFloat(value.quantity),
            returnQuantity: parseFloat(value.returnQuantity),
            purchasePrice: parseFloat(value.purchasePrice),
            salePrice: parseFloat(value.salePrice),
          }
          if (newValue.quantity > 0 || newValue.returnQuantity > 0) {
            if (value.purchasePrice != value.oldPurchasePrice || value.salePrice != value.oldSalePrice) {
              if (newValue.purchasePrice != 0 && newValue.salePrice != 0) {
                var newSalePrices = {...newValue.salePrices}
                for (var key in newSalePrices) {
                  newSalePrices[key] = newValue.salePrice
                }
                // ATL-911: AWAITED. This was a floating promise, and the handler navigates away
                // immediately after — so the product update (and its Supabase dual-write) raced a
                // screen teardown that can cancel it. A price change that silently did not happen
                // is indistinguishable from one that did, on both stores.
                await updateProduct(context.db, newValue, {
                  purchasePrice: newValue.purchasePrice, 
                  salePrices: newSalePrices,
                  dailyStatID: getDailyStatID(store, new Date()),
                  lastChangeBy: context.user.uid,
                  lastChangeAt: new Date(),
                  lastChangeSource: 'supply',
                  lastChangeStore: store,
                  organizationId: context.user.organizationId
                }, productsDict)
              }
            }
            delete newValue.oldPurchasePrice
            delete newValue.oldSalePrice
            delete newValue.productImages
            updatedItems.push(newValue)
          }
        }
        // ATL-958: the per-field check above bounds each LINE. `create_supply_atomic` resolves each
        // line's product and AGGREGATES — `SUM(quantity)` and `SUM(returnQuantity)` GROUP BY the
        // resolved product (ATL-780) — and it is those SUMS that land in
        // `product_movements.increment numeric(10,4)`. Two 600,000 lines naming the same product
        // each pass the per-line check and overflow as 1,200,000, which the backend refuses with a
        // 400 only AFTER the Firestore batch has committed. Refuse here, before it does.
        const unstorableTotal = firstUnstorableStockQuantity(
          updatedItems.map((item) => ({
            ref: item.id,
            name: item.name || item.text,
            values: { quantity: item.quantity, returnQuantity: item.returnQuantity },
          })),
          ['quantity', 'returnQuantity'],
        )
        if (unstorableTotal) {
          setSaveError(t('supplies.totalFieldTooLargeError', {
            productName: unstorableTotal.name || t('notFound'),
            fieldName:
              unstorableTotal.field === 'returnQuantity'
                ? t('supplies.returnQuantity')
                : t('supplies.supplyQuantity'),
            max: MAX_STOCK_QUANTITY,
          }))
          setIsLoading(false)
          return
        }
        const info = {
          supplierID: selectedSupplier.id,
          items: updatedItems,
          supplyDate, 
          dailyStatID: getDailyStatID(store, supplyDate ? supplyDate : new Date()),
          cashierUID: context.user.uid,
          isPaid: isPaid,
          paymentMethod: isPaid ? paymentMethod : null,
          createPaymentTransaction: isPaid ? createPaymentTransaction : null,
          paymentDate: isPaid ? paymentDate : null,
          totalPurchasePrice: totalPurchaseSum(),
          totalReturnSum: totalReturnSum(),
          totalSum: totalSum(),
          invoiceScanRefs: invoiceScanRefs,
          isReturnPresent: isReturnPresent,
          hasPricesUpdated: hasPricesUpdated,
          ...(supplyOrderRef ? { supplyOrderId: supplyOrderRef.id } : {}),
          store: store,
          organizationId: context.user.organizationId
        }
        try {
          const { ref, payment } = await saveNewSupply(db, info)
          // ATL-1503: the cached item carries the leg this save booked — the next payment update's
          // precondition is read from it.
          void addItemToData?.({...supplyAfterPaymentWrite(info, {}, payment), ref, id: ref.id}, info.supplyDate, info.store)
          if (pendingApiDraft) {
            const draft = pendingApiDraft
            setPendingApiDraft(null)
            void reportApiDraftSaved({
              draft,
              organizationId: context.user.organizationId,
              firestoreId: ref.id,
              onRefused: (code) => setApiDraftNotice(t('apiDrafts.linkRefused', { reason: t(apiDraftRefusalKey(code)) })),
            })
          }
          void sendFBAnalyticsEvent('new_supply')
          postHog.capture('supply_created')
          if (stockGuidance && !stockGuidanceConfirmedRef.current) {
            stockGuidanceConfirmedRef.current = true
            captureStockGuidanceReceivingConfirmed(postHog, stockGuidance)
          }
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'supply-create' } })
          console.log(error)
          setSaveError(saveFailureText(error, t, t('error')))
          setIsLoading(false)
        }
      } else {
        if (imageUploading) {
          setSaveError(t('imageIsUploading'))
        } else {
          setSaveError(t('notAllFieldsFilled'))
        }
        setIsLoading(false)
      }
    } else {
      setIsLoading(true)
      if (isPaid && !paymentMethod) {
        setSaveError(t('supplies.choosePaymentMethod'))
        setIsLoading(false)
      } else {
        try {
          let updatedData = {
            isPaid: isPaid,
            paymentMethod: isPaid ? paymentMethod : null,
            createPaymentTransaction: isPaid ? createPaymentTransaction : null,
            paymentDate: isPaid ? paymentDate : null,
            paymentDailyStatID: getDailyStatID(null, isPaid ? paymentDate : null),
            paidBy: context.user.uid,
            organizationId: context.user.organizationId,
          }
          const payment = await updateSupply(db, detail, updatedData, detail)
          // ATL-1503: merge the payment state the write left (its leg id), not only the form fields —
          // a cached item that still says "no leg" after a payment would make the next unpay stale.
          void editItemInData?.(supplyAfterPaymentWrite(detail, updatedData, payment), sectionIndex, itemIndex)
          void sendFBAnalyticsEvent('update_supply')
          postHog.capture('supply_updated')
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'supply-update' } })
          console.log(error)
          setSaveError(t('error'))
          setIsLoading(false)
        }
      }
    }
  }

  const setItemQuantity = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, quantity: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, quantity: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, quantity: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const setReturnItemQuantity = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, returnQuantity: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, returnQuantity: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, returnQuantity: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const setPurchasePrice = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, purchasePrice: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, purchasePrice: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, purchasePrice: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const setSalePrice = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, salePrice: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, salePrice: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, salePrice: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const totalPurchaseSum = () => {
    var sum = 0
    if (items) {
      for (let value of items) {
        sum = sum + ((parseFloat(value.purchasePrice) || 0) * (parseFloat(value.quantity) || 0))
      }
    }
    return sum
  }

  const totalReturnSum = () => {
    var sum = 0
    if (isReturnPresent && items) {
      for (let value of items) {
        sum = sum + ((parseFloat(value.purchasePrice) || 0) * (parseFloat(value.returnQuantity) || 0))
      }
    }
    return sum
  }

  const totalSum = () => {
    return totalPurchaseSum() - totalReturnSum()
  }

  let openImagePickerAsync = async (selectedIndex) => {
    var pickerResult = null
    if (selectedIndex == 0) {
      let permissionResult = await ImagePicker.requestCameraPermissionsAsync()

      if (permissionResult.granted === false) {
        alert('Permission to access camera roll is required!')
        return
      }
  
      // let pickerResult = await ImagePicker.launchImageLibraryAsync()
      pickerResult = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
      })
  
      if (pickerResult.canceled === true) {
        return
      }
    } else {
      // Use system photo picker without requesting broad media library permissions
      pickerResult = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        quality: 1,
      })
  
      if (pickerResult.canceled === true) {
        return
      }
    }
    
    setImageUploading(true)
    const fileName = selectedSupplier.name + ' ' + new Date()

    const result = await manipulateAsync(
      pickerResult.assets[0].uri,
      [{ resize: { width: 1200 } }],
      { compress: 0.6, format: SaveFormat.JPEG }
    )
    try {
      if (Platform.OS === 'web') {
        const blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.onload = function() {
            resolve(xhr.response)
          };
          xhr.onerror = function(e) {
            console.log(e)
            reject(new TypeError('Network request failed'))
          };
          xhr.responseType = 'blob'
          xhr.open('GET', result.uri, true)
          xhr.send(null)
        })
        await uploadSupplyInvoice(blob, fileName)
      } else {
        await uploadSupplyInvoice(result.uri, fileName)
      }
      var newArr = [...invoiceScans]
      newArr.push({ fileName, localUri: result.uri })
      setInvoiceScans(newArr)
    } catch(error) {
      console.log(error)
    }
    setImageUploading(false)
    setImagePickerVisible(false)
  }

  const addItemToSupply = (item, quantity=1, isReturn=false) => {
    // if item present increase quantity
    var newArr = [...items]
    var itemIndex = newArr.findIndex(x => x.id == item.id)
    if (itemIndex > -1) {
      var oldItem = newArr[itemIndex]
      var newItem = {...oldItem, quantity: parseFloat(oldItem.quantity) + (isReturn ? 0 : quantity), returnQuantity: parseFloat(oldItem.returnQuantity) + (isReturn ? quantity : 0)}
      newArr[itemIndex] = newItem
    } else {
      var newItem = {
        id: item.id,
        name: item.name,
        text: item.name,
        quantity: (isReturn ? 0 : quantity),
        returnQuantity: (isReturn ? quantity : 0),
        purchasePrice: item.purchasePrice,
        salePrice: item.salePrice,
        type: item.type,
        unit: item.unit?.name || null,
        barcode: item.barcode != null ? String(item.barcode) : null,
        category: item.categoryID || null,
      }
      newArr.push(newItem)
    }
    setItems(newArr)
    setFilteredItems(newArr)
  }

  const renderBarcodeModeSwitchIcon = (props) => (
    <TouchableOpacity onPress={() => {
      setBarcodeReturnMode(!barcodeReturnMode)
    }}>
      <Icon {...props}  fill={colors.primary} name={barcodeReturnMode ? 'upload-outline' : 'download-outline'}/>
    </TouchableOpacity>
  )

  const handleBarcodeInput = ({nativeEvent}) => {
    nativeEvent?.preventDefault?.()
    let [product, weight] = findProductByBarcode(nativeEvent.text, productsByBarcode)
    if (product) {
      addItemToSupply(product, weight, barcodeReturnMode)
    }
    _barcodeInput.current.clear()
  }
 
  const filterItems = (text) => {
    let newFilteredItems = items?.filter(function(product) {
      return product.name.toLowerCase().indexOf(text.toLowerCase()) > -1;
    })
    setFilteredItems(newFilteredItems)
  }
  
  const renderFooter = () => {
    return (
      <View>
        <Divider />
        {isReturnPresent && (
          <>
            <Text category='p2'>
              {t('supplies.supplyTotal') + ': ' + currencyFormatter.format(totalPurchaseSum())}
            </Text>
            <Text category='p2'>
              {t('supplies.returnTotal') + ': ' + currencyFormatter.format(totalReturnSum())}
            </Text>
          </>
        )}
        <Text category='s1' style={styles.totalSum}>
          {t('supplies.total') + ': ' + currencyFormatter.format(totalSum())}
        </Text>
        
        <Text category='label' style={styles.label}>
          {t('supplies.invoiceScans')}
        </Text>
        <ScrollView horizontal={true} style={styles.invoiceScansContainer}>
          {imageUploading
            ? (
              <View style={styles.spinnerView}>
                <Spinner />
              </View>
            ) : (
              <OverflowMenu
                visible={imagePickerMenuVisible}
                placement='right'
                selectedIndex={null}
                backdropStyle={styles.backdrop}
                disabled={detail != null}
                onSelect={(selectedIndex) => openImagePickerAsync(selectedIndex.row)}
                onBackdropPress={() => setImagePickerVisible(false)}
                anchor={() => (
                  <Button  disabled={detail != null} onPress={() => setImagePickerVisible(true)} style={styles.addMedia} status='basic' accessoryLeft={(style) => (
                    <Icon {...style} fill='#8f9bb3' name='plus-outline'/>
                  )} />
                )}
              >
                <MenuItem title={t('supplies.takePhoto')} />
                <MenuItem title={t('supplies.choosePhoto')} />
              </OverflowMenu>
            )
          }
          {invoiceScans.map((item, index) => (
            item.localUri != null ? (
              <View key={item+index}>  
                <TouchableOpacity onPress={() => {
                  setModalImageURI(item.localUri)
                  setImageModalVisible(true)
                }}> 
                  <Image
                    source={{ uri: item.localUri }}
                    style={styles.thumbnail}
                  />
                </TouchableOpacity>
                {detail == null && (<Button appearance='ghost' status='danger' size='tiny' onPress={async () => {
                  var newInvoiceScans = [...invoiceScans]
                  newInvoiceScans.splice(index, 1)
                  // ATL-911: AWAITED. This is a real Storage delete, and it was fired without
                  // awaiting while the local state was updated immediately after — so the UI
                  // showed the scan gone whether or not the delete actually happened. Same class
                  // as the un-awaited updateProduct below: a write that silently did not happen
                  // is indistinguishable from one that did.
                  await deleteSupplyInvoice(item.fileName)
                  setInvoiceScans(newInvoiceScans)
                }}>
                  {t('delete')}
                </Button>)
                }
              </View>
            ) : (
              <View key={item+index} style={styles.spinnerView}>
                <Spinner />
              </View>
            )
          ))}
        </ScrollView>

        <Modal style={styles.imageModal} visible={imageModalVisible} backdropStyle={styles.backdrop} onBackdropPress={() => setImageModalVisible(false)}>
          {modalImageURI && (<TouchableOpacity onPress={() => setImageModalVisible(false)}><Image
            source={{ uri: modalImageURI }}
            style={styles.fullImage}
          /></TouchableOpacity>)}
        </Modal>

        <Text category='label' style={styles.label}>
          {t('supplies.invoicePayment')}
        </Text>
        <Toggle 
          checked={isPaid}
          onChange={(isChecked) => {
            setIsPaid(isChecked)
            if (!isChecked) {
              setPaymentMethod(null)
            }
          }}
          style={styles.toggle}
        >
          {isPaid ? t('supplies.paid') : t('supplies.notPaid')}
        </Toggle>
          
        {isPaid && (paymentMethod?.id || paymentMethod == null) && (  // old supplies had hard coded payment methods and didn't have id
          <View>
            <View style={styles.radioGroup}>
              { constants?.wallets.data.map((item, index) => (
                <Radio key={item+index} style={styles.radio} checked={item.id === paymentMethod?.id} onChange={() => setPaymentMethod(item)}>
                  {item.text}
                </Radio>
              ))}
            </View>
            <Text category='label' style={styles.label}>
              {t('supplies.paymentDate')}
            </Text>
            <Datepicker
              placement='top'
              date={paymentDate}
              dateService={localeDateService}
              onSelect={(date) => {
                setPaymentDate(date)
                _datePicker.current.blur()
              }}
              min={new Date('2020-12-01')}
              max={new Date('2029-12-31')}
              ref={_datePicker}
            />
            <CheckBox 
              style={{marginTop: 5}}
              text=''
              checked={createPaymentTransaction}
              onChange={setCreatePaymentTransaction}
            >     
              {t('supplies.createPaymentTransaction')}
            </CheckBox>
          </View>
        )}

        { Platform.OS === 'web' && detail && (
          <Button appearance='outline' style={styles.printButton} onPress={() => {navigation.navigate('PrintStickers', {products: detail.items})}}>{t('supplies.stickers')}</Button>
        )}

        {pendingApiDraft && (
          <Text category='p1' status='info' style={styles.label} testID='supply-api-draft-banner'>
            {t('apiDrafts.acceptanceBanner', { name: pendingApiDraft.proposedBy?.tokenName || t('apiDrafts.agent') })}
          </Text>
        )}
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        {apiDraftNotice != '' && (
          <Text category='p1' status='warning' style={styles.label}>
            {apiDraftNotice}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => {
            if (!supplyOrderRef) {
              navigation.goBack()
            } else {
              navigation.navigate('Home') // supply order detail is not updated so better go to supplies
            }
          }} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
        !successMessageVisible && <Button onPress={save} style={styles.submitButton}>
          {t('save')}
        </Button>
        )}
      </View>
    )
  }

  const fields = [
    {
      key: 'name',
      type: 'text',
      columnStyle: 'nameColumn',
      title: t('supplies.name'),
      textCategory: 's1'
    },
    {
      key: 'quantity',
      type: 'quantity',
      keyboardType: 'decimal-pad',
      title: t('supplies.supplyQuantity'),
      caption: item => (productUnitsDict[item.productUnit]?.shortName || t('pcs')),
      onChange: (index, value) => setItemQuantity(index, value)
    },
    ...(isReturnPresent ? [{
      key: 'returnQuantity',
      type: 'quantity',
      keyboardType: 'decimal-pad',
      title: t('supplies.returnQuantity'),
      caption: item => (productUnitsDict[item.productUnit]?.shortName || t('pcs')),
      onChange: (index, text) => setReturnItemQuantity(index, text),
      controlledInput: true
    }] : []),
    ...(hasPricesUpdated ? [{
      key: 'purchasePrice',
      type: 'input',
      keyboardType: 'decimal-pad',
      title: t('supplies.purchasePrice'),
      onChange: (index, text) => setPurchasePrice(index, text),
      caption: currency,
      formatter: value => currencyFormatter.format(value)
    }] : [{
      key: 'purchasePrice',
      type: 'text',
      title: t('supplies.purchasePrice'),
      caption: currency,
      formatter: value => currencyFormatter.format(value)
    }]),
    ...(hasPricesUpdated ? [{
      key: 'salePrice',
      type: 'input',
      keyboardType: 'decimal-pad',
      title: t('supplies.sellPrice'),
      caption: currency,
      onChange: (index, text) => setSalePrice(index, text)
    }] : []),
    {
      key: 'totalPrice',
      type: 'text',
      columnStyle: 'totalColumn',
      title: t('supplies.totalPrice'),
      textCategory: 's1',
      caption: currency,
      formatter: (value, item) => currencyFormatter.format(item.quantity * item.purchasePrice)
    }
  ]

  return (
     <KeyboardAwareScrollView style={{flex: 1}} keyboardShouldPersistTaps='handled' keyboardDismissMode='none' extraHeight={100}>
      <View style={styles.container}>
        {detail && <Text appearance='hint' style={{textAlign: 'center', marginVertical: 10}}>
          {detail.id}
        </Text>}
        { !detail && context.user.role === 'admin' && 
          <Button style={{alignSelf: 'flex-end', marginRight: -20}} size='small' appearance='ghost' onPress={() => {
              void Linking.openURL(t('supplies.instructionsLink'))
            }} accessoryLeft={(props) => (<Icon name='question-mark-circle-outline' {...props} 
            style={{...props.style, marginRight: 0}} size='small' />)}
          >
            {t('supplies.instructions')}
          </Button>
        }
        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label'>
            {t('supplier')}
          </Text>
          { (userRole?.permissions?.['editSuppliers'] || context.user.role === 'admin') && (
            <TouchableOpacity onPress={() => navigation.navigate('UniversalList', {atom: suppliersDictAtom, searchField: 'name', detailScreen: 'SupplierDetail', title: t('suppliers')})} style={{marginLeft: 5, padding: 3}}>
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )}
        </View>
        <PlatformDependantSelect
          data={Object.values(suppliersDict)}
          selectedOption={selectedSupplier}
          disabled={detail != null}
          onSelect={(itemValue, itemIndex) => {
            setSelectedSupplier(itemValue)
            setItems(productsBySuppliers[itemValue.id])
            setFilteredItems(productsBySuppliers[itemValue.id])
          }}
          withSearch={true}
        />

        {selectedSupplier && (
          <View>
            { Object.values(storesDict).length > 1 && (userRole?.permissions?.['viewOtherStores'] || context.user.role === 'admin') && (
              <>
                <Text category='label' style={styles.label}>
                  {t('store')}
                </Text>
                <PlatformDependantSelect
                  data={Object.values(storesDict)}
                  selectedOption={storesDict?.[store]}
                  disabled={detail != null}
                  onSelect={(itemValue, itemIndex) => {
                    setStore(itemValue.id)
                  }}
                />
              </>
            )}
            <Text category='label' style={styles.label}>
              {t('supplies.supplyDate')}
            </Text>
            <Datepicker
              placement='top'
              dateService={localeDateService}
              date={detail ? getLocalStoreDate(store, supplyDate) : supplyDate}
              onSelect={(date) => {
                let currentDate = new Date()
                date.setHours(currentDate.getHours(), currentDate.getMinutes())
                setSupplyDate(date)
                _datePicker.current.blur()
              }}
              min={new Date('2020-12-01')}
              max={new Date('2029-12-31')}
              ref={_datePicker}
              disabled={detail != null}
            />
            <Text category='label' style={styles.label}>
              {t('supplies.supplyReturnIsPresent')}
            </Text>
            <Toggle 
              checked={isReturnPresent}
              onChange={(checked) => {
                setIsReturnPresent(checked)
              }}
              style={styles.toggle}
              disabled={detail != null}
            >
              {isReturnPresent ? t('supplies.returnPresentTrue') : t('supplies.returnPresentFalse')}
            </Toggle>

            <Text category='label' style={styles.label}>
              {t('supplies.supplyPricesHaveUpdated')}
            </Text>
            <Toggle 
              checked={hasPricesUpdated}
              onChange={(checked) => {
                setHasPricesUpdated(checked)
              }}
              style={styles.toggle}
              disabled={detail != null}
            >
              {hasPricesUpdated ? t('supplies.pricesUpdatedTrue') :  t('supplies.pricesUpdatedFalse')}
            </Toggle>
            <Text category='label' style={styles.label}>
              {t('supplies.supplyItems')}
            </Text>
            { detail == null && (
              <View style={{flexDirection: 'row', gap: 10, marginBottom: 10, flexWrap: 'wrap'}}>
                <Input 
                  key={'search'}
                  placeholder={t('search')}
                  onChangeText={filterItems}
                  accessoryLeft={(props) => (<Icon name='search-outline' {...props} style={{...props.style, marginRight: 0}} />)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  returnKeyType='done'
                  style={{minWidth: 200, flexGrow: 1}}
                />
                <Input 
                  key={'barcode'}
                  ref={_barcodeInput}
                  placeholder={t('supplies.fillInBarcode')}
                  autoCapitalize="none"
                  blurOnSubmit={false}
                  selectTextOnFocus={true}
                  returnKeyType='done'
                  accessoryLeft={(props) => (<Icon name='grid-outline' {...props} style={{...props.style, marginRight: 0}} />)}
                  accessoryRight={renderBarcodeModeSwitchIcon}
                  autoCorrect={false}
                  onSubmitEditing={handleBarcodeInput}
                  caption={evaProps => <Text {...evaProps}>{barcodeReturnMode ? t('supplies.returnMode') : t('supplies.supplyMode')}</Text>}
                  style={{minWidth: 200, flexGrow: 1}}
                />
              </View>
            )}
            <AdaptiveTableCard
              data={filteredItems}
              fields={fields}
              disabled={detail != null}
              breakpoint={hasPricesUpdated ? 950 : 800}
              renderFooter={renderFooter}
            />
          </View>
        )}    
      </View>
      <View style={{height: 100}}></View>
     </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  }, 
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  toggle: {
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  submitButton: {
    marginTop: 15,
    marginBottom: 60,
  },
  totalSum: {
    marginBottom: 16,
  },
  invoiceScansContainer: {
    // flexDirection: 'row'
  },
  thumbnail: {
    width: 150,
    height: 150,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: '#8f9bb3',
    marginRight: 10,
    resizeMode: 'contain',
  },
  addMedia: {
    width: 150,
    height: 150,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: '#8f9bb3',
    marginRight: 10,
  },
  spinnerView: {
    width: 150,
    height: 150,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    borderWidth: 1,
    borderRadius: 10,
    borderColor: '#8f9bb3',
    marginRight: 10,
  },
  fullImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  imageModal: {
    width: '90%',
    height: '90%',
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center',
    marginBottom: 60,
  },
  printButton: {
    marginTop: 15,
  },
  radioGroup: {
    marginTop: 5
  },
  radio: {
    marginBottom: 5,
  },
})